import React from 'react';
import { trackEvent } from '@/lib/analytics';

/* =====================================================================
   StoreBadges — App Store / Google Play indirme rozetleri
   Soft-slate zemin + hairline çerçeve (primary CTA'dan ayrışsın diye).
   Google Play işareti kendi 4 renginde, Apple glifi tek renk (resmi kullanım).

   Kullanım:
     <StoreBadges apps="admin" />        → App Store + Google Play
     <StoreBadges apps="loyalty" />      → App Store + Google Play
     <StoreBadges apps="admin" tone="dark" />  → koyu zemin üstünde (beyaz rozet)
   ===================================================================== */

export const STORE_LINKS = {
  admin: {
    ios: 'https://apps.apple.com/tr/app/tilki-otomasyon/id6778756612?l=tr',
    android: 'https://play.google.com/store/apps/details?id=cafe.otomasyon.frontend&hl=tr',
  },
  loyalty: {
    ios: 'https://apps.apple.com/tr/app/tilki-sadakat/id6798203928?l=tr',
    android: 'https://play.google.com/store/apps/details?id=cafe.otomasyon.sadakat&hl=tr',
  },
};

const AppleGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0" aria-hidden="true">
    <path d="M17.05 12.53c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.72-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.87.69 1.18-.02 1.93-1.08 2.65-2.14.83-1.22 1.18-2.41 1.2-2.47-.03-.01-2.3-.88-2.32-3.5zM14.87 5.6c.6-.74 1.01-1.75.9-2.77-.87.04-1.94.59-2.57 1.31-.56.64-1.06 1.68-.93 2.67.97.08 1.97-.49 2.6-1.21z" />
  </svg>
);

const PlayGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" className="flex-shrink-0" aria-hidden="true">
    <path
      d="M3.6 1.84c-.26.28-.41.71-.41 1.27v17.78c0 .56.15.99.41 1.27l.06.06 9.96-9.96v-.24L3.66 1.78l-.06.06z"
      fill="#00A0FF"
    />
    <path
      d="M17.3 15.7l-3.32-3.32v-.24l3.33-3.33.08.05 3.93 2.24c1.12.64 1.12 1.68 0 2.32l-3.93 2.23-.09.05z"
      fill="#FFBC00"
    />
    <path d="M17.39 15.65l-3.41-3.41-10.02 10.02c.37.39.98.44 1.66.05l11.77-6.66" fill="#F9414D" />
    <path d="M17.39 8.35 5.62 1.69c-.68-.39-1.29-.34-1.66.05l10.02 10.02 3.41-3.41z" fill="#00E676" />
  </svg>
);

const Badge = ({ href, glyph, label, tone, event }) => {
  const base =
    'inline-flex items-center gap-2.5 rounded-lg px-4 py-2.5 flex-1 min-w-[150px] transition-all duration-200';
  const skin =
    tone === 'dark'
      ? 'bg-white border border-white/20 text-ink hover:shadow-card-lift'
      : 'bg-surface-2 border border-hairline text-ink hover:border-ink/25 hover:shadow-card';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent('store-badge-click', { store: label })}
      className={`${base} ${skin}`}
    >
      {glyph}
      <span className="leading-tight">
        <span className="block text-[9.5px] text-ink-muted font-inter">İndir</span>
        <span className="block font-poppins text-sm font-semibold">{label}</span>
      </span>
    </a>
  );
};

export default function StoreBadges({ apps = 'admin', tone = 'light', className = '' }) {
  const links = STORE_LINKS[apps];

  return (
    <div className={className}>
      <div
        className={`text-[11px] font-semibold uppercase tracking-[0.12em] font-inter mb-3 ${
          tone === 'dark' ? 'text-white/55' : 'text-ink-muted'
        }`}
      >
        Mobil Uygulamayı İndir
      </div>

      <div className="flex flex-wrap gap-2.5">
        <Badge href={links.ios} glyph={<AppleGlyph />} label="App Store" tone={tone} />
        <Badge href={links.android} glyph={<PlayGlyph />} label="Google Play" tone={tone} />
      </div>
    </div>
  );
}
